import { useEffect, useRef, useState } from 'react'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { useDebounce } from '@/utils/fetch'
import { setTimeoutInDev } from '@/utils/environment'
import apiFetch from '@/services/apiService'
import SuggestionsPortal from '@/components/SuggestionsPortal'
import { Badge } from '@/components/ui'
import { formatPhoneNumber } from '@/utils/formatters'
import { formatBienAddress, getBienSuggestionLabel } from '../biens/biensSlice'

export default ({}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [loading, setLoading] = useState(false)
  const inputRef = useRef(null)

  const fetchSuggestions = useDebounce(async (term) => {
    if (!term || term.length < 2) {
      setSuggestions([])
      return
    }
    
    setLoading(true)
    try {
      let query = { term }
      
      await setTimeoutInDev()

      const contactsPromise = apiFetch()
        .url(`contacts/search`)
        .query(query)
        .get()
        .then( res =>  res.data.map(contact => {
          let description = `${contact.email}`
          if(description.length > 0 && contact.telephone) {
            description += ' | '
          }
          description += `${contact.telephone ? 'Tel: ' : ''}${formatPhoneNumber(contact.telephone)}`
          return ({
            id: contact.id,
            type: "contact",
            label: contact.nom_complet,
            description: description.trim(),
            url: contact.links?.show || null
          })
        }))
        .catch(error => {
          console.error('Erreur lors de la recherche de contacts:', error)
          return [] // Valeur par défaut en cas d'erreur
        })
      
      const biensPromise = apiFetch()
        .url(`biens/search`)
        .query(query)
        .get()
        .then( res => res.data.map( bien => {
          const label = getBienSuggestionLabel(bien)
          return {
            id: bien.id,
            type: bien.type_bien || 'bien',
            label,
            url: bien.links?.show || null
          }
        }))
        .catch(error => {
          console.error('Erreur lors de la recherche de biens:', error)
          return [] // Valeur par défaut en cas d'erreur
        })

      const [contacts, biens] = await Promise.all([contactsPromise, biensPromise])
        
      setSuggestions([...contacts, ...biens] || [])
    } catch (error) {
      console.error('Erreur lors de la recherche de contacts:', error)
      setSuggestions([])
    } finally {
      setLoading(false)
    }
  }, 300)

  useEffect(() => {
    if(searchTerm.length == 0) {
      setShowSuggestions(false)
      setSuggestions([])
    }
  }, [searchTerm])

  const handleInputChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)
    fetchSuggestions(value)
    setShowSuggestions(true)
  }

  return (
    <div className="w-full">
      <label className="input w-full">
        <MagnifyingGlassIcon className='h-[1em] opacity-50 text-slate-600' />
        <input 
          type="search" 
          className="grow w-96 text-slate-800" 
          placeholder="Recherche d'un contact, d'un bien" 
          ref={inputRef}
          onChange={handleInputChange}
          value={searchTerm}
        />
        {loading && <span className="loading loading-spinner loading-sm text-base-content/70 absolute right-2 top-1/2 transform -translate-y-1/2"></span>}
        {!loading && searchTerm.length > 0 && (
          <button
          type="button"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-base-content/70 rounded-full w-6 h-6 transition-colors duration-200 ease-in hover:bg-gray-200 focus:outline-none focus:bg-gray-300 dark:text-white dark:hover:bg-dark-500 dark:focus:bg-dark-450 hover:bg-gray-200"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setSearchTerm('')
            }}
          >
            <XMarkIcon className='m-auto h-[1em] w-[1em]'/>
        </button>
        )}
      </label>
      
      <SuggestionsPortal
        show={showSuggestions && (suggestions.length > 0 || (searchTerm.length >= 2 && !loading))}
        anchorRef={inputRef}
      >
      {suggestions.length > 0 ? (
        <div className="absolute z-10 w-full bg-base-100 shadow-lg rounded-lg mt-1 border border-base-300 max-h-60 overflow-y-auto">
          {suggestions.map((item, index) => (
            <div 
              key={index}
              className="p-2 hover:bg-base-200 cursor-pointer"
              onClick={() => item.url ? window.location.href = item.url : console.error("no url for this item")}
            >
              <div>
                <Badge text={item.type} />
                <span className="ml-2">{item.label.length > 0 ? item.label : item.description}</span>
              </div>
              { item.label.length > 0 && <div className="text-sm text-base-content/70">
                  {item.description}
                </div>
              }
            </div>
          ))}
        </div>
      ) : (
        <div className="absolute z-10 w-full bg-base-100 shadow-lg rounded-lg mt-1 border border-base-300 p-2 space-y-2">
          <p>Aucun contact ou bien trouvé</p>
        </div>
      )}
      </SuggestionsPortal>

    </div>
  )
}